import { intlLocale } from '@saniplus/api-client';
import type { CurrentShift, Locale, MeContextResponse } from '@saniplus/contracts';
import {
  Button,
  Card,
  CircleAlert,
  Calendar,
  Clock,
  IconButton,
  MapPin,
  RefreshCw,
  StateField,
  TriangleAlert,
  X,
  decorativeIcon,
  type StatusTone,
} from '@saniplus/ui';
import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { refusalMessageKey, refusalWindow, type QueueState } from '../offline';
import { FactList, PanelHeading, type Fact } from './components/FactList';
import { PunchWindowRail } from './components/PunchWindowRail';
import { SupervisorAction } from './components/SupervisorAction';
import type { RecoveryView } from './lib/conditions';
import {
  clockParts,
  dayNumber,
  elapsedParts,
  longDate,
  minutesOf,
  monthName,
  msUntil,
  shortDate,
  siteTimezoneOf,
} from './lib/format';

/**
 * W8 — the recovery screens, as Variant A: **a state band inside the app
 * shell**, with the rail and the bottom navigation kept.
 *
 * That choice is the whole judgement (`W8-recovery/JUDGEMENT.md` §4). Two of
 * the conditions — offline-at-punch and GPS-denied — are *informational*: the
 * punch is (or will be) recorded and the worker should carry on. A takeover
 * with no nav would read as a dead end on both, so this keeps the shell, gives
 * those two a `×` and a « Continuer », and ends every genuinely blocking
 * condition in a named supervisor.
 *
 * Three rules it enforces:
 *
 * - **No error code ever reaches a worker.** Every sentence is a key in this
 *   feature's catalogue, and the fallback for a code with no dedicated screen
 *   is `refusalMessageKey()` from the offline engine — never the code itself.
 * - **A missing value is a dash with a sentence, never a zero** — « Sortie
 *   pointée → aucune — », « Position → aucune — ».
 * - **Account lockout is not here.** `deriveRecovery` refuses login-only
 *   codes; a locked worker cannot reach *Mes quarts*, and drawing this shell
 *   around that message would offer four doors that are shut (§6.1.2).
 */
export interface RecoveryScreenProps {
  readonly recovery: RecoveryView;
  readonly context: MeContextResponse | null;
  readonly queue: QueueState;
  readonly now: number;
  readonly locale: Locale;
  /** Leaves the screen; acknowledges the refusal when there is one. */
  readonly onDismiss: () => void;
  /** From the GPS screen only: punch with the permission still refused. */
  readonly onPunchAnyway: () => void;
  readonly onRequestLocation: () => void;
  /** « Réessayer » — the device screen's ghost action. */
  readonly onRetry: () => void;
  readonly shiftsPath?: string;
  readonly employeeName?: string;
  readonly employeeNumber?: string;
}

interface Screen {
  readonly word: string;
  readonly cap: string;
  readonly value: string;
  readonly valueScale: 'duration' | 'clock';
  readonly valueUnit?: string;
  readonly icon: ReactNode;
  readonly lead: string;
  readonly support: string;
  readonly panelTitle: string;
  readonly panelChip?: { readonly tone: StatusTone; readonly label: string };
  readonly facts: readonly Fact[];
  readonly panelExtra?: ReactNode;
  readonly footnote?: string;
  readonly actions: ReactNode;
}

export function RecoveryScreen({
  recovery,
  context,
  queue,
  now,
  locale,
  onDismiss,
  onPunchAnyway,
  onRequestLocation,
  onRetry,
  shiftsPath = '/quarts',
  employeeName,
  employeeNumber,
}: RecoveryScreenProps) {
  const { t } = useTranslation('punch');
  const container = useRef<HTMLDivElement>(null);

  /*
   * This screen REPLACES the home screen, subtree and all, so the state band's
   * `role="status"` arrives already populated — a live region inserted with
   * its content announces nothing, and the confirm sheet has just tried to
   * restore focus to a punch key that no longer exists, dropping it on
   * `<body>`. Moving focus here says the refused punch out loud and puts the
   * next Tab on this screen's own actions. Keyed on the condition, so a second
   * refusal arriving over the first is announced too.
   */
  useEffect(() => {
    container.current?.focus();
  }, [recovery.kind]);

  const shift: CurrentShift | null = context?.currentShift ?? null;
  const session = context?.openSession ?? null;
  const zone = siteTimezoneOf(context);

  const clock = (iso: string | null | undefined): string => {
    if (iso === null || iso === undefined) return '';
    const parts = clockParts(iso, zone);
    return parts === null ? '' : t('format.clock', parts);
  };
  const span = (ms: number): string => {
    const total = minutesOf(ms);
    return total >= 60 ? t('format.duration', elapsedParts(ms)) : t('format.minutes', { count: total });
  };
  const shiftRange = (value: CurrentShift): string =>
    `${clock(value.startsAt)} ${t('format.arrow')} ${clock(value.endsAt)}`;

  const attemptAt = recovery.attemptedAt;
  const nowIso = new Date(now).toISOString();

  /*
   * The call ends every screen — but only as the PRIMARY on the six that
   * block. On `offline` and `gpsDenied` the punch is safe and the primary is
   * « Continuer » / « Pointer quand même »; a second filled key there is two
   * primaries on one surface, and it tells a worker whose punch is queued that
   * they need to phone somebody. `recovery.informational` already names those
   * two, so the emphasis follows it rather than a second list that could drift.
   */
  const supervisorSlot = (subLabel?: string): ReactElement => (
    <SupervisorAction
      contacts={context?.supervisorContacts}
      emphasis={recovery.informational ? 'quiet' : 'key'}
      {...(subLabel === undefined ? {} : { subLabel })}
    />
  );

  /*
   * A control that changes ROUTE is an `<a>`, drawn with `Button`'s quiet
   * recipe rather than a hand-copied class string. `asChild` puts the recipe
   * on the `<Link>` itself: an anchor inside a `<button>` is two interactive
   * elements, which is an accessibility defect and not a styling shortcut.
   */
  const shiftsLink = (label: string): ReactElement => (
    <Button asChild variant="quiet" size="lg" fullWidth>
      <Link to={shiftsPath}>{label}</Link>
    </Button>
  );

  const screen: Screen = (() => {
    switch (recovery.kind) {
      // ── 1 · outside the 60/60 window ───────────────────────────────────
      case 'outsideWindow': {
        /*
         * The refusal's own bounds first, the live context only as a fallback.
         * `error.details` carries the window the server actually judged this
         * punch against; a refusal is retained until the worker acknowledges
         * it (I-7), so by the time this screen is read `/me/context` may be
         * describing the next shift's window — or none at all.
         */
        const refused = refusalWindow(recovery.failure?.details);
        const opensAt = refused?.windowOpensAt ?? context?.punchability.windowOpensAt ?? null;
        const closesAt = refused?.windowClosesAt ?? context?.punchability.windowClosesAt ?? null;
        const untilOpen = opensAt === null ? null : msUntil(opensAt, now);
        const late = untilOpen === null || untilOpen <= 0;
        const deficitMs =
          attemptAt === null || opensAt === null
            ? null
            : late && closesAt !== null
              ? Date.parse(attemptAt) - Date.parse(closesAt)
              : Date.parse(opensAt) - Date.parse(attemptAt);
        const windowMs =
          opensAt === null || closesAt === null ? null : Date.parse(closesAt) - Date.parse(opensAt);

        return {
          word: late ? t('recovery.outsideWindow.wordLate') : t('recovery.outsideWindow.word'),
          cap: late ? t('recovery.outsideWindow.capLate') : t('recovery.outsideWindow.cap'),
          value: deficitMs === null ? '' : String(minutesOf(deficitMs)),
          valueScale: 'duration',
          valueUnit: t('format.minutesUnit'),
          icon: <TriangleAlert {...decorativeIcon('lg')} />,
          lead: late
            ? t('recovery.outsideWindow.leadLate', {
                closesAt: clock(closesAt),
                now: clock(nowIso),
              })
            : t('recovery.outsideWindow.lead', {
                opensAt: clock(opensAt),
                now: clock(attemptAt ?? nowIso),
              }),
          support: late
            ? t('recovery.outsideWindow.supportLate')
            : t('recovery.outsideWindow.support'),
          panelTitle: t('recovery.outsideWindow.panel'),
          facts:
            shift === null
              ? []
              : [
                  {
                    key: 'shift',
                    label: t('recovery.staleOpen.shift'),
                    value: shiftRange(shift),
                  },
                  {
                    key: 'site',
                    label: t('recovery.gpsDenied.site'),
                    value: shift.site.name,
                    prose: true,
                  },
                  {
                    key: 'nightOf',
                    label: t('recovery.staleOpen.nightOf'),
                    value: longDate(shift.startsAt, zone, locale),
                    prose: true,
                  },
                ],
          panelExtra:
            opensAt === null || closesAt === null || attemptAt === null ? undefined : (
              <PunchWindowRail
                opensAt={opensAt}
                closesAt={closesAt}
                attemptAt={attemptAt}
                opensLabel={clock(opensAt)}
                closesLabel={clock(closesAt)}
                attemptLabel={clock(attemptAt)}
                attemptCaption={`${t('recovery.outsideWindow.attempt')} · ${
                  deficitMs === null
                    ? ''
                    : late
                      ? t('recovery.outsideWindow.deficitLate', { duration: span(deficitMs) })
                      : t('recovery.outsideWindow.deficit', { duration: span(deficitMs) })
                }`}
                windowCaption={
                  windowMs === null
                    ? ''
                    : t('recovery.outsideWindow.windowOpenFor', { duration: span(windowMs) })
                }
                railLabel={t('recovery.outsideWindow.railLabel', {
                  opensAt: clock(opensAt),
                  closesAt: clock(closesAt),
                })}
              />
            ),
          actions: (
            <>
              {supervisorSlot()}
              <Button variant="quiet" size="lg" fullWidth onClick={onDismiss}>
                {t('action.backHome')}
              </Button>
            </>
          ),
        };
      }

      // ── 2 · a shift that was never closed (D-08) ───────────────────────
      case 'staleOpen': {
        const openedAt = session?.openedAt ?? null;
        return {
          word: t('recovery.staleOpen.word'),
          cap: t('recovery.staleOpen.cap'),
          value: openedAt === null ? '' : dayNumber(openedAt, zone),
          valueScale: 'duration',
          ...(openedAt === null ? {} : { valueUnit: monthName(openedAt, zone, locale) }),
          icon: <TriangleAlert {...decorativeIcon('lg')} />,
          /*
           * No date, no date-shaped sentence.
           *
           * `staleOpen` normally arrives with the open session that caused it,
           * but the code can also reach this screen from a punch the server
           * refused, and then there is nothing to interpolate — which produced
           * « Votre quart du n'a jamais été fermé. » An ungrammatical sentence
           * on a blocking screen is a worker reading it twice and trusting it
           * less; the undated wording says the same thing and parses.
           */
          lead:
            openedAt === null
              ? t('recovery.staleOpen.leadUndated')
              : t('recovery.staleOpen.lead', { date: longDate(openedAt, zone, locale) }),
          support: t('recovery.staleOpen.support'),
          panelTitle: t('recovery.staleOpen.panel'),
          panelChip: { tone: 'danger', label: t('chip.blocking') },
          facts: [
            ...(openedAt === null
              ? []
              : [
                  {
                    key: 'nightOf',
                    label: t('recovery.staleOpen.nightOf'),
                    value: longDate(openedAt, zone, locale),
                    prose: true,
                  } satisfies Fact,
                ]),
            ...(shift === null
              ? []
              : [
                  {
                    key: 'shift',
                    label: t('recovery.staleOpen.shift'),
                    value: shiftRange(shift),
                  } satisfies Fact,
                ]),
            {
              key: 'in',
              label: t('recovery.staleOpen.punchedIn'),
              // A blank cell is the one thing a payroll fact may never be
              // (D-12): with no instant to show, this is the dash-and-sentence
              // model, exactly like the punch-out row under it.
              value: openedAt === null ? null : clock(openedAt),
              ...(openedAt === null ? { nullLabel: t('recovery.staleOpen.noInLabel') } : {}),
            },
            {
              key: 'out',
              label: t('recovery.staleOpen.punchedOut'),
              value: null,
              nullLabel: t('recovery.staleOpen.noneLabel'),
            },
            ...(session === null
              ? []
              : [
                  {
                    key: 'site',
                    label: t('recovery.gpsDenied.site'),
                    value: session.site.name,
                    prose: true,
                  } satisfies Fact,
                ]),
          ],
          footnote: t('recovery.staleOpen.footnote'),
          actions: (
            <>
              {supervisorSlot()}
              {shiftsLink(t('action.seeMyShifts'))}
            </>
          ),
        };
      }

      // ── 3 · queued because there was no network (informational) ────────
      case 'offline': {
        const queued = queue.pending[queue.pending.length - 1] ?? null;
        const recordedAt = queued?.deviceRecordedAt ?? attemptAt;
        return {
          word:
            recovery.type === 'in'
              ? t('recovery.offline.wordIn')
              : t('recovery.offline.wordOut'),
          cap: t('recovery.offline.cap'),
          value: clock(recordedAt),
          valueScale: 'clock',
          icon: <Clock {...decorativeIcon('lg')} />,
          lead:
            recovery.type === 'in'
              ? t('recovery.offline.leadIn', { time: clock(recordedAt) })
              : t('recovery.offline.leadOut', { time: clock(recordedAt) }),
          support:
            recovery.type === 'in'
              ? t('recovery.offline.support')
              : t('recovery.offline.supportOut'),
          panelTitle: t('recovery.offline.panel'),
          facts: [
            { key: 'recordedAt', label: t('recovery.offline.recordedAt'), value: clock(recordedAt) },
            ...(shift === null
              ? []
              : [
                  {
                    key: 'shift',
                    label: t('recovery.offline.shift'),
                    value: shiftRange(shift),
                  } satisfies Fact,
                  {
                    key: 'nightOf',
                    label: t('recovery.offline.nightOf'),
                    value: longDate(shift.startsAt, zone, locale),
                    prose: true,
                  } satisfies Fact,
                ]),
            {
              key: 'queue',
              label: t('recovery.offline.queue'),
              value: t('recovery.offline.queueCount', { count: queue.pending.length }),
            },
          ],
          footnote: t('recovery.offline.footnote'),
          actions: (
            <>
              <Button
                size="lg"
                variant="primary"
                fullWidth
                subLabel={t('action.continueSub')}
                onClick={onDismiss}
                data-testid="recovery-primary"
              >
                {t('action.continue')}
              </Button>
              {supervisorSlot()}
            </>
          ),
        };
      }

      // ── 4 · nothing scheduled ──────────────────────────────────────────
      case 'noShift': {
        const upcoming = context?.upcomingShifts ?? [];
        const next = upcoming[0] ?? null;
        return {
          word: t('recovery.noShift.word'),
          cap:
            next === null
              ? t('recovery.noShift.capNone')
              : t('recovery.noShift.cap', {
                  day: new Intl.DateTimeFormat(intlLocale(locale), {
                    timeZone: next.site.timezone,
                    weekday: 'long',
                  })
                    .format(new Date(next.startsAt))
                    .toUpperCase(),
                }),
          value: next === null ? '' : clock(next.startsAt),
          valueScale: 'clock',
          icon: <Calendar {...decorativeIcon('lg')} />,
          lead: t('recovery.noShift.lead'),
          support: t('recovery.noShift.support'),
          panelTitle: t('recovery.noShift.panel'),
          panelChip: { tone: 'neutral', label: t('chip.night') },
          /*
           * An EMPTY SCHEDULE is not a missing value.
           *
           * It used to be a `FactList` row whose label and whose null value
           * were the same sentence, so the panel read « Aucun quart prév… —
           * Aucun quart prévu pour l'instant. »: the sentence twice, once
           * truncated to 116 px and once in danger ink. The dash-in-red model
           * is D-12's — a value that was *due* and is missing — and nothing is
           * due here. The home screen already draws this case as one quiet
           * line; this now matches it.
           */
          ...(upcoming.length === 0
            ? {
                facts: [],
                panelExtra: (
                  <p className="text-body-compact text-ink-muted">
                    {t('recovery.noShift.empty')}
                  </p>
                ),
              }
            : {
                facts: upcoming.slice(0, 4).map((item) => ({
                  key: item.id,
                  label: longDate(item.startsAt, item.site.timezone, locale),
                  value: shiftRange(item),
                })),
              }),
          footnote: t('recovery.noShift.footnote'),
          actions: (
            <>
              {supervisorSlot()}
              {shiftsLink(t('action.seeAllShifts'))}
            </>
          ),
        };
      }

      // ── 5 · this phone is not the bound one (D-06) ─────────────────────
      case 'deviceNotRegistered':
        return {
          word: t('recovery.deviceNotRegistered.word'),
          cap: t('recovery.deviceNotRegistered.cap'),
          value: clock(attemptAt),
          valueScale: 'clock',
          icon: <CircleAlert {...decorativeIcon('lg')} />,
          lead: t('recovery.deviceNotRegistered.lead'),
          support: t('recovery.deviceNotRegistered.support'),
          panelTitle: t('recovery.deviceNotRegistered.panel'),
          panelChip: { tone: 'danger', label: t('chip.blocking') },
          facts: [
            ...(employeeName === undefined
              ? []
              : [
                  {
                    key: 'employee',
                    label: t('recovery.deviceNotRegistered.employee'),
                    value: employeeName,
                    prose: true,
                  } satisfies Fact,
                ]),
            ...(employeeNumber === undefined
              ? []
              : [
                  {
                    key: 'number',
                    label: t('recovery.deviceNotRegistered.employeeNumber'),
                    value: employeeNumber,
                  } satisfies Fact,
                ]),
            {
              key: 'thisPhone',
              label: t('recovery.deviceNotRegistered.thisPhone'),
              value: t('recovery.deviceNotRegistered.notRegistered'),
              prose: true,
              tone: 'danger',
            },
            {
              key: 'attempt',
              label: t('recovery.deviceNotRegistered.attempt'),
              value: clock(attemptAt),
            },
          ],
          footnote: t('recovery.deviceNotRegistered.footnote'),
          actions: (
            <>
              {supervisorSlot(t('supervisor.canAuthorize'))}
              <Button variant="quiet" size="lg" fullWidth onClick={onRetry}>
                {t('action.retry')}
              </Button>
            </>
          ),
        };

      // ── 6 · the location permission is refused (informational) ─────────
      case 'gpsDenied':
        return {
          word: t('recovery.gpsDenied.word'),
          cap: t('recovery.gpsDenied.cap'),
          value: clock(attemptAt ?? nowIso),
          valueScale: 'clock',
          icon: <MapPin {...decorativeIcon('lg')} />,
          lead: t('recovery.gpsDenied.lead'),
          support: t('recovery.gpsDenied.support'),
          panelTitle: t('recovery.gpsDenied.panel'),
          // §6.1.1: « À vérifier » is a WARNING in the status table. Drawing it
          // on the info field would put the client's identity colour behind a
          // word the system defines as a warning.
          panelChip: { tone: 'warning', label: t('chip.toReview') },
          facts: [
            {
              key: 'position',
              label: t('recovery.gpsDenied.position'),
              value: null,
              nullLabel: t('recovery.gpsDenied.noneLabel'),
            },
            { key: 'time', label: t('recovery.gpsDenied.time'), value: clock(attemptAt ?? nowIso) },
            ...(shift === null
              ? []
              : [
                  {
                    key: 'shift',
                    label: t('recovery.gpsDenied.shift'),
                    value: shiftRange(shift),
                  } satisfies Fact,
                  {
                    key: 'site',
                    label: t('recovery.gpsDenied.site'),
                    value: shift.site.name,
                    prose: true,
                  } satisfies Fact,
                ]),
          ],
          footnote: t('recovery.gpsDenied.footnote'),
          actions: (
            <>
              <Button
                size="lg"
                variant="primary"
                fullWidth
                subLabel={t('action.punchAnywaySub')}
                onClick={onPunchAnyway}
                data-testid="recovery-primary"
              >
                {recovery.type === 'in' ? t('action.punchInAnyway') : t('action.punchOutAnyway')}
              </Button>
              <Button size="lg" variant="secondary" fullWidth onClick={onRequestLocation}>
                {t('action.enableLocation')}
              </Button>
              {supervisorSlot()}
            </>
          ),
        };

      // ── 7 · a queued punch the server refused later, on sync ───────────
      case 'syncRefused': {
        const failure = recovery.failure;
        const at = failure?.deviceRecordedAt ?? attemptAt;
        const date = at === null ? '' : longDate(at, zone, locale);
        return {
          word: t('recovery.syncRefused.word'),
          cap:
            recovery.type === 'in'
              ? t('recovery.syncRefused.capIn', {
                  date: at === null ? '' : shortDate(at, zone, locale).toUpperCase(),
                })
              : t('recovery.syncRefused.capOut', {
                  date: at === null ? '' : shortDate(at, zone, locale).toUpperCase(),
                }),
          value: clock(at),
          valueScale: 'clock',
          icon: <RefreshCw {...decorativeIcon('lg')} />,
          lead:
            recovery.type === 'in'
              ? t('recovery.syncRefused.leadIn', { date, time: clock(at) })
              : t('recovery.syncRefused.leadOut', { date, time: clock(at) }),
          support: t('recovery.syncRefused.support'),
          panelTitle: t('recovery.syncRefused.panel'),
          panelChip: { tone: 'danger', label: t('chip.blocking') },
          facts: [
            {
              key: 'type',
              label: t('recovery.syncRefused.type'),
              value:
                recovery.type === 'in'
                  ? t('recovery.syncRefused.typeIn')
                  : t('recovery.syncRefused.typeOut'),
              prose: true,
            },
            { key: 'phoneTime', label: t('recovery.syncRefused.phoneTime'), value: clock(at) },
            ...(shift === null
              ? []
              : [
                  {
                    key: 'shift',
                    label: t('recovery.syncRefused.shift'),
                    value: shiftRange(shift),
                  } satisfies Fact,
                ]),
            ...(at === null
              ? []
              : [
                  {
                    key: 'nightOf',
                    label: t('recovery.syncRefused.nightOf'),
                    value: date,
                    prose: true,
                  } satisfies Fact,
                ]),
            ...(failure === null
              ? []
              : [
                  {
                    key: 'refusedAt',
                    label: t('recovery.syncRefused.refusedAt'),
                    value: clock(failure.refusedAt),
                  } satisfies Fact,
                ]),
          ],
          footnote: t('recovery.syncRefused.footnote'),
          actions: (
            <>
              {supervisorSlot()}
              <Button
                variant="quiet"
                size="lg"
                fullWidth
                onClick={onDismiss}
                data-testid="recovery-acknowledge"
              >
                {t('action.acknowledge')}
              </Button>
              {shiftsLink(t('action.seeMyShifts'))}
            </>
          ),
        };
      }

      // ── 8 · every other refusal the server can make ────────────────────
      default:
        return {
          word: t('recovery.refused.word'),
          cap: t('recovery.refused.cap'),
          value: clock(attemptAt ?? nowIso),
          valueScale: 'clock',
          icon: <CircleAlert {...decorativeIcon('lg')} />,
          // The engine owns the sentence for every refusal code, and it is a
          // sentence — never the code (`TRACK-D`: no error codes for workers).
          lead:
            recovery.refusalCode === null
              ? t('recovery.refused.support')
              : t(refusalMessageKey(recovery.refusalCode)),
          support: t('recovery.refused.support'),
          panelTitle: t('recovery.refused.panel'),
          panelChip: { tone: 'danger', label: t('chip.blocking') },
          facts: [
            {
              key: 'type',
              label: t('recovery.refused.type'),
              value:
                recovery.type === 'in'
                  ? t('recovery.syncRefused.typeIn')
                  : t('recovery.syncRefused.typeOut'),
              prose: true,
            },
            { key: 'time', label: t('recovery.refused.time'), value: clock(attemptAt ?? nowIso) },
          ],
          actions: (
            <>
              {supervisorSlot()}
              <Button
                variant="quiet"
                size="lg"
                fullWidth
                onClick={onDismiss}
                data-testid="recovery-acknowledge"
              >
                {t('action.acknowledge')}
              </Button>
            </>
          ),
        };
    }
  })();

  return (
    <div
      ref={container}
      tabIndex={-1}
      className="flex min-h-full flex-col gap-3 focus:outline-none"
      data-testid="punch-recovery"
    >
      {/*
        * A TEST hook, and therefore `aria-hidden`.
        *
        * `sr-only` does not mean hidden — it means invisible and still
        * announced. Without this, VoiceOver reads the raw state-machine name
        * out loud ("deviceNotRegistered") to the one worker who cannot
        * see the screen it describes. The state is already announced properly,
        * in French, by the panel below.
        */}
      <span data-testid="recovery-kind" aria-hidden="true" className="sr-only">
        {recovery.kind}
      </span>

      {/* Full-bleed: the band spans `main`'s gutter, which is what makes it a
          band rather than a card. The home screen deliberately does NOT do
          this — an alarm colour on screen every night stops being read. */}
      <div className="-ms-3.5 -me-3.5 -mt-3">
        <StateField
          tone={recovery.tone}
          word={screen.word}
          value={screen.value === '' ? undefined : screen.value}
          valueScale={screen.valueScale}
          icon={screen.icon}
          /*
           * The right-hand cap, on the word's own line — W8 JUDGEMENT §G1:
           * *glyph · word · one number · right-hand cap at `label`*. It was
           * passed as `detail` (a line under the number) only because the slot
           * did not exist when this screen was built.
           */
          cap={<span className="text-label uppercase">{screen.cap}</span>}
        />
      </div>

      {recovery.informational ? (
        <div className="flex justify-end">
          <IconButton
            label={t('action.dismiss')}
            /*
             * `lg` — 48 px, which `IconButton` documents as "the worker floor".
             * The default is `md` (40 px, the admin size), and this `×` is the
             * only way off the two informational screens: a gloved thumb at
             * 02:00 gets one target, and it was eight pixels short of the
             * product's own rule. Measured by QA-2 at 360/390/768.
             */
            size="lg"
            onClick={onDismiss}
            data-testid="recovery-close"
          >
            <X {...decorativeIcon('md')} />
          </IconButton>
        </div>
      ) : null}

      <Card padding="phone">
        <h1 className="text-body text-ink">{screen.lead}</h1>
        <p className="mt-2 text-body text-ink-muted">{screen.support}</p>
      </Card>

      {screen.facts.length === 0 && screen.panelExtra === undefined ? null : (
        <Card padding="phone">
          <PanelHeading
            title={screen.panelTitle}
            {...(screen.panelChip === undefined ? {} : { chip: screen.panelChip })}
          />
          {screen.panelExtra === undefined ? null : (
            <div className="mt-2.5">{screen.panelExtra}</div>
          )}
          {screen.facts.length === 0 ? null : (
            <FactList facts={screen.facts} className="mt-2.5" />
          )}
          {screen.footnote === undefined ? null : (
            <p className="mt-2.5 border-t border-hairline pt-2.5 text-body text-ink-muted">
              {screen.footnote}
            </p>
          )}
        </Card>
      )}

      {/* §6.2: on a refusal `main` may scroll — a refusal can carry six facts
          — but the action stack never does. */}
      <div className="sticky bottom-0 z-10 mt-auto flex flex-col gap-2 border-t border-hairline bg-canvas pb-1 pt-2">
        {screen.actions}
      </div>
    </div>
  );
}
